import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { C, Field, FieldLabel, Input } from "./styles";

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`from { transform: translateY(-16px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1300;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: ${fadeIn} 150ms ease;
`;

const Dialog = styled.div`
    background: ${C.surface};
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.18);
    width: 100%;
    max-width: 420px;
    animation: ${slideIn} 150ms ease;
    font-family: "Inter", system-ui, sans-serif;
    overflow: hidden;
`;

const DialogHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid ${C.border};
`;

const DialogTitle = styled.h2`
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: ${C.text};
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #94a3b8;
    font-size: 1.1rem;
    line-height: 1;
    padding: 4px;
    border-radius: 6px;
    &:hover { background: ${C.bg}; color: ${C.text}; }
`;

const DialogBody = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const DialogFooter = styled.div`
    padding: 14px 20px;
    border-top: 1px solid ${C.border};
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const DialogBtn = styled.button`
    padding: 7px 18px;
    border-radius: 7px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: pointer;
    transition: background 120ms;
    border: ${({ $primary }) => $primary ? "none" : `1px solid ${C.border}`};
    background: ${({ $primary }) => $primary ? C.indigo : C.surface};
    color: ${({ $primary }) => $primary ? "#fff" : "#374151"};
    &:hover { background: ${({ $primary }) => $primary ? C.indigoD : C.bg}; }
`;

// onExit(value) — value is the entered username when confirmed, undefined when
// cancelled. Mirrors the previous AddUserModalForm contract so WorkspaceAdmin's
// add-user flow is unchanged.
export function AddUserModal({ open, onExit }) {
    const [value, setValue] = useState("");

    useEffect(() => {
        if (open) setValue("");
    }, [open]);

    if (!open) return null;

    function confirm() {
        onExit(value);
    }

    function cancel() {
        onExit();
    }

    return (
        <Overlay onClick={e => { if (e.target === e.currentTarget) cancel(); }}>
            <Dialog>
                <DialogHeader>
                    <DialogTitle>Add user to workspace</DialogTitle>
                    <CloseBtn onClick={cancel} aria-label="Close">✕</CloseBtn>
                </DialogHeader>
                <DialogBody>
                    <Field>
                        <FieldLabel>Username</FieldLabel>
                        <Input
                            type="text"
                            name="value"
                            placeholder="user@example.com"
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            autoFocus
                        />
                    </Field>
                </DialogBody>
                <DialogFooter>
                    <DialogBtn onClick={cancel}>Cancel</DialogBtn>
                    <DialogBtn $primary onClick={confirm}>Add</DialogBtn>
                </DialogFooter>
            </Dialog>
        </Overlay>
    );
}
